import type { BatchSummary } from '@shared/models/BatchSummary';

// Modelo para el cliente del pedido
export interface OrderCustomer {
  name: string;
  phone: string;
  email: string;
}

// Modelo para el envío del pedido
export interface OrderShipment {
  courier_id: number;
  tracking_number: string;
}

// Modelo para el pedido identificado
export interface IdentifiedOrder {
  id: number;
  ref: string;
  external_id: string;
  shop_id: number;
  status_id: number;
  total: number;
  customer: OrderCustomer;
  shipment: OrderShipment;
  shipment_statuses_count: number;
  lines: OrderLine[];
}

// Modelo para producto en línea
export interface OrderLineProduct {
  id: number;
  name: string;
  code: string;
  ean?: string;
  image?: string;
  image_hash?: string;
}

// Modelo para línea de pack (productos dentro de un pack)
export interface PackLine {
  product_id: number;
  qty: number;
  product?: OrderLineProduct;
}

// Modelo para pack
export interface Pack {
  id: number;
  title: string;
  pack_version_id: number;
  lines: PackLine[];
}

// Modelo para las líneas del pedido
export interface OrderLine {
  id: number;
  product_id: number | null;
  name: string | null;
  qty: number;
  price?: number;
  product?: OrderLineProduct;
  pack_id: number | null;
  pack_version_id: number | null;
  pack?: Pack;
}

// Request para identificar un pedido
export interface IdentifyOrderRequest {
  barcode: string;
  courier_id: number;
}

// Pasos del flujo de recepción
// 1. cart: Escanear y validar carrito
// 2. identify: Escanear código de paquete + seleccionar courier para identificar pedido
// 3. lines: Escanear líneas del pedido (futuro)
export type ReceiveStep = 'cart' | 'identify' | 'lines';

// Estado del flujo de recepción
export interface ReceiveFlowState {
  currentStep: ReceiveStep;
  validatedCart: Cart | null;
  selectedCourierId: number | null;
  identifiedOrder: IdentifiedOrder | null;
  isProcessing: boolean;
  error: string | null;
}

// Estado para el paso de identificación (paso 2)
export interface IdentifyStepState {
  barcode: string | null;
  courierId: number | null;
}

// Modelo para el courier
export interface CourierOption {
  id: number;
  name: string;
}

// Modelo para stock en carrito
export interface StockLocationCart {
  location_id: number;
  product_id: number;
  qty: number;
  returned_reception_id: number | null;
  preparation_id: number | null;
  merchandise_id: number | null;
  location_order_id: number | null;
  batch_id?: number | null;
  batch?: BatchSummary | null;
  product?: {
    id: number;
    name: string;
    image?: string;
    code: string;
  };
}

// Modelo para el carrito
export interface Cart {
  id: number;
  code: string;
  team_id: number;
  type_id: number;
  type?: {
    id: number;
    name: string;
  };
  is_locked: boolean;
  stock: number;
  stock_location_carts: StockLocationCart[];
}

// Modelo de recepción (para mostrar en la vista)
export interface ReturnedReception {
  id: number;
  status: number;
  team_id: string | number;
  total_orders: number;
  orders_count?: number;
  code: string;
  created_at: string;
  updated_at: string;
}

// Status de línea de recepción
export type ReceptionLineStatus = 'ACEPTADO' | 'RECHAZADO';

// Razones de rechazo
export enum RejectionReason {
  PRODUCTO_NO_ESTA_EN_EL_PEDIDO = 'PRODUCTO_NO_ESTA_EN_EL_PEDIDO',
  PRODUCTO_ROTO = 'PRODUCTO_ROTO',
  PACKAGING_DEFECTUOSO = 'PACKAGING_DEFECTUOSO',
  FALTAN_PARTES = 'FALTAN_PARTES',
  OTRO = 'OTRO',
}

// Tipos de incidencia de courier
export enum IncidenceType {
  CRUCE_ETIQUETAS = 'CRUCE_ETIQUETAS',
  PEDIDO_MANIPULADO = 'PEDIDO_MANIPULADO',
}

// Razones de rechazo con labels
export const REJECTION_REASONS: { value: RejectionReason; label: string }[] = [
  { value: RejectionReason.PRODUCTO_NO_ESTA_EN_EL_PEDIDO, label: 'Producto no está en el pedido' },
  { value: RejectionReason.PRODUCTO_ROTO, label: 'Producto roto' },
  { value: RejectionReason.PACKAGING_DEFECTUOSO, label: 'Packaging defectuoso' },
  { value: RejectionReason.FALTAN_PARTES, label: 'Faltan partes' },
  { value: RejectionReason.OTRO, label: 'Otro' },
];

// Tipos de incidencia de courier con labels
export const COURIER_INCIDENCE_TYPES: { value: IncidenceType; label: string }[] = [
  { value: IncidenceType.CRUCE_ETIQUETAS, label: 'Cruce de etiquetas' },
  { value: IncidenceType.PEDIDO_MANIPULADO, label: 'Pedido manipulado' },
];

// Modelo para línea de recepción
export interface ReceivedLine {
  id: number;
  product_id: number;
  quantity: number;
  status: 'ACEPTADO' | 'RECHAZADO';
  rejection_reason: RejectionReason | null;
  rejected_imgs: string[] | null;
  cart_id: number;
  stock_location_order_id: number | null;
  order_id: number;
  created_at: string;
  updated_at: string;
  product?: OrderLineProduct;
  cart?: {
    id: number;
    code: string;
  };
  order?: {
    id: number;
    ref: string;
    external_id: string;
    total: string;
    currency: string;
    status_id: number;
    shop?: {
      id: number;
      name: string;
      team?: {
        id: number;
        name: string;
      };
    };
  };
  returned_reception_order?: {
    id: number;
    returned_reception_id: number;
    order_id: number;
    processed_at: string;
    processed_by: number;
    processed_by_user?: {
      id: number;
      name: string;
      email: string;
    };
    has_courier_incidence: boolean;
    incidence_type: string | null;
    created_at: string;
    updated_at: string;
  };
}

// Respuesta paginada de líneas
export interface ReceivedLinesResponse {
  data: ReceivedLine[];
  meta: {
    total: number;
    perPage: number;
    currentPage: number;
    lastPage: number;
  };
  links: {
    first: string;
    prev: string | null;
    next: string | null;
    last: string;
  };
}

// Modelos para pedidos procesados
export interface ProcessedOrderUser {
  id: number;
  name: string;
  email: string;
}

export interface ProcessedOrderShop {
  id: number;
  name: string;
  team: {
    id: number;
    name: string;
  } | null;
}

export interface ProcessedOrderData {
  id: number;
  ref: string;
  external_id: string;
  total: number;
  currency: string;
  status_id: number;
  shop: ProcessedOrderShop | null;
}

export interface ProcessedOrder {
  id: number;
  returned_reception_id: number;
  order_id: number;
  processed_at: string;
  processed_by: number | null;
  processed_by_user: ProcessedOrderUser | null;
  has_courier_incidence: boolean;
  incidence_type: string | null;
  created_at: string;
  updated_at: string;
  order: ProcessedOrderData | null;
}

export interface ProcessedOrdersResponse {
  data: ProcessedOrder[];
  meta: {
    total: number;
    perPage: number;
    currentPage: number;
    lastPage: number;
  };
  links: {
    first: string;
    prev: string | null;
    next: string | null;
    last: string;
  };
}

// Modelos para órdenes de ubicación
export interface LocationOrderUser {
  id: number;
  name: string;
  email: string;
}

export interface LocationOrderLine {
  id: number;
  location_order_id: number;
  product_id: number;
  qty_planned: number;
  qty_moved: number;
  qty_pending: number;
}

export interface LocationOrderCart {
  id: number;
  code: string;
}

export interface LocationOrder {
  id: number;
  team_id: number;
  cart_id: number;
  cart?: LocationOrderCart;
  order_type: string;
  source_type: string;
  source_id: number;
  status: 'pending' | 'in_progress' | 'done' | 'cancelled';
  created_by_id: number;
  created_by_user: LocationOrderUser | null;
  notes: string | null;
  total_products?: number;
  total_quantity?: number;
  created_at: string;
  updated_at: string;
  lines: LocationOrderLine[];
}

export interface LocationOrdersResponse {
  data: LocationOrder[];
  meta: {
    total: number;
    perPage: number;
    currentPage: number;
    lastPage: number;
  };
  links: {
    first: string;
    prev: string | null;
    next: string | null;
    last: string;
  };
}

// Línea de recepción para el request
export interface ReceiveOrderLineRequest {
  product_id: number;
  quantity: number;
  status: ReceptionLineStatus;
  rejection_reason: RejectionReason | null;
  rejected_imgs: File[] | null;
  stock_location_order_id: number | null;
  batch_id?: number;
}

// Request para recepcionar un pedido
export interface ReceiveOrderRequest {
  order_id: number;
  cart_id: number;
  lines: ReceiveOrderLineRequest[];
  has_courier_incidence: boolean;
  incidence_type: string | null;
}

// Request para crear orden de ubicación
export interface CreateLocationOrderRequest {
  cart_id: number;
  notes?: string;
}

// Response al crear orden de ubicación
export interface CreateLocationOrderResponse {
  id: number;
  team_id: number;
  cart_id: number;
  order_type: string;
  source_type: string;
  source_id: number;
  status: 'pending' | 'in_progress' | 'done' | 'cancelled';
  created_by_id: number;
  notes: string | null;
  lines: Array<{
    id: number;
    location_order_id: number;
    product_id: number;
    qty_planned: number;
    qty_moved: number;
    qty_pending: number;
  }>;
}

// Carrito con estadísticas
export interface CartWithStats {
  id: number;
  code: string;
  total_products: number;
  total_quantity: number;
  has_location_order: boolean;
  location_order_id: number | null;
}

export interface CartsResponse {
  data: CartWithStats[];
}
